import type { ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useBookingFlowContext } from './BookingFlowProvider';
import type { BookingFlowStatus } from './useBookingFlow';

export interface BookingFlowStepperProps {
  /** Varsayılan: context'ten. */
  steps?: { id: string; label: string }[];
  activeStep?: number;
  status?: BookingFlowStatus;
  /** Yalnız tamamlanmış adımlara geri gitme (ileri devre dışı). Verilmezse tıklanamaz. */
  onStepClick?: (index: number) => void;
  /** Sağ uçta içerik — süre geri sayım çipi buraya. */
  rightSlot?: ReactNode;
  orientation?: 'horizontal' | 'compact' | 'auto';
}

/** Yeniden tasarlanmış booking stepper — mobilde "Adım 2 / 5 · Yolcu Bilgisi" +
 *  ilerleme çubuğu; sm+ tam yatay stepper (aktif+running adımda spinner, hata ikonu).
 *  Zorunlu prop yok; varsayılan `useBookingFlowContext()` okur. */
export function BookingFlowStepper({
  steps: stepsProp,
  activeStep: activeStepProp,
  status: statusProp,
  onStepClick,
  rightSlot,
  orientation = 'auto',
}: BookingFlowStepperProps) {
  const ctx = useBookingFlowContext();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = stepsProp ?? ctx.steps.map((s) => ({ id: s.id, label: s.label }));
  const activeStep = activeStepProp ?? ctx.stepIndex;
  const status = statusProp ?? ctx.status;

  const compact = orientation === 'compact' || (orientation === 'auto' && isNarrow);
  const total = steps.length;
  const current = steps[Math.min(activeStep, total - 1)];

  if (compact) {
    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">
            Adım {activeStep + 1} / {total}
            {current ? ` · ${current.label}` : ''}
          </Typography>
          {rightSlot}
        </Box>
        <LinearProgress
          variant="determinate"
          value={total > 1 ? (activeStep / (total - 1)) * 100 : 0}
          color={status === 'error' ? 'error' : 'primary'}
          sx={{ borderRadius: 1, height: 6 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ flexGrow: 1 }}>
        {steps.map((step, i) => {
          const isActive = i === activeStep;
          const clickable = !!onStepClick && i < activeStep;
          return (
            <Step key={step.id} completed={i < activeStep}>
              <StepLabel
                onClick={clickable ? () => onStepClick(i) : undefined}
                sx={clickable ? { cursor: 'pointer' } : undefined}
                error={isActive && status === 'error'}
                slotProps={
                  isActive && status === 'running'
                    ? { stepIcon: { icon: <CircularProgress size={18} /> } }
                    : isActive && status === 'error'
                      ? { stepIcon: { icon: <ErrorOutlineIcon color="error" fontSize="small" /> } }
                      : undefined
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
      {rightSlot && <Box sx={{ flexShrink: 0, pt: 0.5 }}>{rightSlot}</Box>}
    </Box>
  );
}
